import React, { useEffect, useMemo, useState } from "react";

const USER_TYPES = ["cleaner", "admin", "organiser"];

const IcleanViewUsers = ({ usersMode, currentUser, embedded = false, createTrigger = 0, rootLocations = [] }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [email, setEmail] = useState("");
  const [type, setType] = useState("cleaner");
  const [editingUser, setEditingUser] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState("");
  const [cropZoom, setCropZoom] = useState(1);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [savingPhoto, setSavingPhoto] = useState(false);

  const canManageUsers = currentUser?.type === "admin" || currentUser?.is_iclean_admin;

  const companyName = useMemo(
    () => currentUser?.company?.name || currentUser?.selectedCompany?.name || "-",
    [currentUser]
  );

  const loadUsers = async () => {
    if (!usersMode || !canManageUsers) return;
    setLoading(true);
    try {
      const response = await fetch("/api/users", { credentials: "include" });
      if (response.status === 401) {
        setUsers([]);
        return;
      }
      const data = await response.json();
      if (data.success) {
        const fetchedUsers = data.users || [];
        setUsers(fetchedUsers);
        setSelectedUserId((prev) => {
          if (!prev) return null;
          return fetchedUsers.some((u) => u.user_id === prev) ? prev : null;
        });
      }
    } catch (err) {
      console.error("Users list load hiba:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersMode, canManageUsers]);

  useEffect(() => {
    if (createTrigger > 0 && canManageUsers) setShowCreateForm(true);
  }, [createTrigger, canManageUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;

    try {
      const response = await fetch("/api/users", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, type }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.detail || "The user could not be created.");
        return;
      }
      if (data.success) {
        setEmail("");
        setType("cleaner");
        setShowCreateForm(false);
        await loadUsers();
      }
    } catch (err) {
      console.error("User create hiba:", err);
      alert("An error occurred while creating the user.");
    }
  };

  const handleTypeChange = async (targetUserId, nextType) => {
    setEditingUser((prev) => (prev ? { ...prev, type: nextType } : prev));
  };

  const handleFieldChange = (field, value) => {
    setEditingUser((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveSelectedUser = async () => {
    if (!editingUser) return;
    try {
      const response = await fetch(`/api/users/${editingUser.user_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editingUser.name || "",
          type: editingUser.type || "cleaner",
          status: editingUser.status || "active",
          root_location_ids: editingUser.root_location_ids || [],
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.detail || "The user details could not be saved.");
        return;
      }
      if (data.success) {
        setUsers((prev) =>
          prev.map((u) => (u.user_id === data.user.user_id ? { ...u, ...data.user } : u))
        );
        setEditingUser((prev) => (prev ? { ...prev, ...data.user } : prev));
      }
    } catch (err) {
      console.error("User save hiba:", err);
      alert("An error occurred while saving the user.");
    }
  };

  const TYPE_LABEL = { cleaner: "cleaning", admin: "admin", organiser: "organiser" };

  const sortedUsers = [...users].sort((a, b) => {
    if (b.is_logged_in !== a.is_logged_in) return b.is_logged_in ? 1 : -1;
    return a.user_id - b.user_id;
  });

  useEffect(() => {
    if (!selectedUserId) {
      setEditingUser(null);
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
      setPhotoPreviewUrl("");
      return;
    }
    const user = users.find((u) => u.user_id === selectedUserId) || null;
    setEditingUser(user ? { ...user } : null);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl("");
    setCropZoom(1);
    setCropX(0);
    setCropY(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, users]);

  const cropSource = photoPreviewUrl || editingUser?.photo_url || "";

  const handlePhotoSelect = (file) => {
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    if (!file) {
      setPhotoPreviewUrl("");
      return;
    }
    const nextUrl = URL.createObjectURL(file);
    setPhotoPreviewUrl(nextUrl);
    setCropZoom(1);
    setCropX(0);
    setCropY(0);
  };

  const handleSavePhotoCrop = async () => {
    if (!editingUser || !cropSource) return;
    setSavingPhoto(true);
    try {
      const sourceImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = cropSource;
      });

      const cropSize = 256;
      const canvas = document.createElement("canvas");
      canvas.width = cropSize;
      canvas.height = cropSize;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        alert("The image could not be processed.");
        return;
      }

      const baseScale = Math.max(cropSize / sourceImg.width, cropSize / sourceImg.height);
      const renderedWidth = sourceImg.width * baseScale * cropZoom;
      const renderedHeight = sourceImg.height * baseScale * cropZoom;
      const drawX = (cropSize - renderedWidth) / 2 + cropX;
      const drawY = (cropSize - renderedHeight) / 2 + cropY;
      ctx.drawImage(sourceImg, drawX, drawY, renderedWidth, renderedHeight);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      if (!blob) {
        alert("The crop could not be saved.");
        return;
      }

      const formData = new FormData();
      formData.append("photo", blob, "user-avatar.jpg");

      const response = await fetch(`/api/users/${editingUser.user_id}/photo`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.detail || "The user photo could not be saved.");
        return;
      }
      if (data.success) {
        setUsers((prev) =>
          prev.map((u) => (u.user_id === data.user.user_id ? { ...u, ...data.user } : u))
        );
        setEditingUser((prev) => (prev ? { ...prev, ...data.user } : prev));
        if (photoPreviewUrl) {
          URL.revokeObjectURL(photoPreviewUrl);
          setPhotoPreviewUrl("");
        }
      }
    } catch (err) {
      console.error("User photo save hiba:", err);
      alert("An error occurred while saving the user photo.");
    } finally {
      setSavingPhoto(false);
    }
  };

  if (!usersMode) return null;

  if (!canManageUsers) {
    return (
      <section className={embedded ? "p-3" : "p-3 border-b border-slate-800"}>
        <div className="text-[0.75rem] font-semibold text-slate-100 mb-2">Users</div>
        <div className="text-[0.7rem] text-slate-400">
          Only administrators can manage user accounts.
        </div>
      </section>
    );
  }

  return (
    <section className={`${embedded ? "p-3" : "p-3 border-b border-slate-800"} space-y-3`}>
      {!embedded && <div className="text-[0.75rem] font-semibold text-slate-100">Users</div>}
      <div className="text-[0.65rem] text-slate-400">Company: {companyName}</div>

      {(!embedded || showCreateForm) && (
      <div className="space-y-2 rounded-lg border border-slate-700 bg-slate-950/40 p-2">
        {!embedded && (
          <button
            onClick={() => setShowCreateForm((prev) => !prev)}
            className={`w-full px-2 py-1 rounded border text-[0.7rem] ${
              showCreateForm
                ? "bg-amber-300 text-black border-amber-400"
                : "bg-slate-800 text-slate-100 border-slate-600 hover:bg-slate-700"
            }`}
          >
            {showCreateForm ? "Create user..." : "+ Create user"}
          </button>
        )}

        {showCreateForm && (
          <form onSubmit={handleCreate} className="space-y-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@company.com"
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[0.7rem] text-slate-100"
            />
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[0.7rem] text-slate-100"
            >
              {USER_TYPES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="w-full px-2 py-1 rounded border text-[0.7rem] bg-teal-700 text-slate-100 border-teal-500 hover:bg-teal-600"
            >
              Save new user
            </button>
          </form>
        )}
      </div>
      )}

      <div className="space-y-2 rounded-lg border border-slate-700 bg-slate-950/40 p-2">
        <div className="text-[0.65rem] text-slate-300">
          Existing users {loading ? "(loading...)" : `(${users.length})`}
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
          {sortedUsers.map((u) => (
            <button
              key={u.user_id}
              onClick={() =>
                setSelectedUserId((prev) => (prev === u.user_id ? null : u.user_id))
              }
              className={`w-full text-left px-2 py-1 rounded border text-[0.65rem] ${
                selectedUserId === u.user_id
                  ? "bg-red-700/30 border-red-400 text-red-100"
                  : "bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
              }`}
            >
              <div className="flex items-center gap-2 w-full">
                <div className="w-6 h-6 rounded-full overflow-hidden border border-slate-600 bg-slate-700 flex items-center justify-center shrink-0">
                  {u.photo_url ? (
                    <img
                      src={u.photo_url}
                      alt="User avatar"
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <span className="text-[0.6rem] font-semibold text-slate-100">
                      {(u.name || u.email || "U").trim()[0]?.toUpperCase() || "U"}
                    </span>
                  )}
                </div>
                <span className="truncate flex-1">
                  {u.name || "Unnamed user"}
                  {u.type && (
                    <span className="ml-1 text-slate-500">
                      ({TYPE_LABEL[u.type] || u.type})
                    </span>
                  )}
                </span>
                {u.is_logged_in && (
                  <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" title="Logged in" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {editingUser && (
        <div className="space-y-2 mb-1 p-2 border border-slate-700 rounded bg-slate-800/40">
          <div className="text-[0.6rem] text-slate-400">
            Selected user ({editingUser.user_id})
          </div>
          <input
            type="text"
            value={editingUser.name || ""}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
            placeholder="User name"
          />
          <input
            type="text"
            value={editingUser.email || ""}
            readOnly
            className="w-full text-[0.7rem] bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-400"
          />
          <select
            value={editingUser.type || "cleaner"}
            onChange={(e) => handleTypeChange(editingUser.user_id, e.target.value)}
            className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
          >
            {USER_TYPES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <select
            value={editingUser.status || "active"}
            onChange={(e) => handleFieldChange("status", e.target.value)}
            className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="archived">archived</option>
            <option value="banned">banned</option>
          </select>
          {rootLocations.length > 0 && (
            <div className="space-y-1 rounded border border-slate-700 bg-slate-900/50 p-2">
              <div className="text-[0.6rem] text-slate-400">Root locations (none ticked: shown everywhere)</div>
              {rootLocations.map((root) => {
                const ids = editingUser.root_location_ids || [];
                const checked = ids.some((id) => String(id) === String(root.id));
                return (
                  <label key={root.id} className="flex items-center gap-2 text-[0.7rem] text-slate-200">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleFieldChange(
                        "root_location_ids",
                        checked ? ids.filter((id) => String(id) !== String(root.id)) : [...ids, root.id]
                      )}
                      className="h-3.5 w-3.5 accent-cyan-500"
                    />
                    <span>{root.name}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="text-[0.6rem] text-slate-500">
            {editingUser.profile_completed ? "profile completed" : "first login pending"}
          </div>
          <div className="space-y-2 border border-slate-700 rounded p-2 bg-slate-900/50">
            <div className="text-[0.6rem] text-slate-400">User avatar crop</div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handlePhotoSelect(e.target.files?.[0] || null)}
              className="w-full text-[0.65rem] text-slate-300"
            />
            {cropSource ? (
              <>
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border border-slate-600 bg-slate-800 relative">
                  <img
                    src={cropSource}
                    alt="User crop preview"
                    className="absolute left-1/2 top-1/2 min-w-full min-h-full pointer-events-none"
                    style={{
                      transform: `translate(-50%, -50%) translate(${cropX}px, ${cropY}px) scale(${cropZoom})`,
                      transformOrigin: "center center",
                    }}
                    draggable={false}
                  />
                </div>
                <label className="block text-[0.6rem] text-slate-400">
                  Zoom
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={cropZoom}
                    onChange={(e) => setCropZoom(Number(e.target.value))}
                    className="w-full"
                  />
                </label>
                <label className="block text-[0.6rem] text-slate-400">
                  Position X
                  <input
                    type="range"
                    min="-120"
                    max="120"
                    step="1"
                    value={cropX}
                    onChange={(e) => setCropX(Number(e.target.value))}
                    className="w-full"
                  />
                </label>
                <label className="block text-[0.6rem] text-slate-400">
                  Position Y
                  <input
                    type="range"
                    min="-120"
                    max="120"
                    step="1"
                    value={cropY}
                    onChange={(e) => setCropY(Number(e.target.value))}
                    className="w-full"
                  />
                </label>
                <button
                  onClick={handleSavePhotoCrop}
                  disabled={savingPhoto}
                  className="w-full px-2 py-1 rounded border text-[0.7rem] bg-teal-700 text-slate-100 border-teal-500 hover:bg-teal-600 disabled:opacity-60"
                >
                  {savingPhoto ? "Saving photo..." : "Save avatar crop"}
                </button>
              </>
            ) : (
              <div className="text-[0.6rem] text-slate-500">
                No photo selected. Upload a new image or keep the current avatar.
              </div>
            )}
          </div>
          <button
            onClick={handleSaveSelectedUser}
            className="w-full px-2 py-1 rounded border text-[0.7rem] bg-teal-700 text-slate-100 border-teal-500 hover:bg-teal-600"
          >
            Save user
          </button>
        </div>
      )}
    </section>
  );
};

export default IcleanViewUsers;
